import * as Sentry from '@sentry/browser';

import type { RefundAudit, RefundAuditAnswers, RefundAuditFlow } from '../types';

// The audit is best-effort: every helper swallows its own errors so a refund is never blocked by it.
function reportAuditError(error: unknown) {
  try {
    console.warn('Refund audit capture failed', error);
    Sentry.captureException(error, { tags: { feature: 'refund_audit' } });
  } catch {
    // Reporting must never throw either
  }
}

// Steps shared by every refund flow, in wizard order
const REFUND_AUDIT_STEPS = {
  REASON: { order: 0, name: 'Reason' },
  DETAIL: { order: 1, name: 'Refund details' },
  PAYER_ALLOCATION: { order: 2, name: 'Payer allocation' },
  CONFIRM: { order: 3, name: 'Confirm' },
} as const;

export type RefundAuditStepKey = keyof typeof REFUND_AUDIT_STEPS;

export function createRefundAudit(flow: RefundAuditFlow): RefundAudit {
  return { flow, steps: [] };
}

/**
 * Raw values exactly as the form submitted them. Repeated keys (checkbox groups) become arrays; files are dropped.
 */
export function formDataToAnswers(formData: FormData): RefundAuditAnswers {
  const answers: RefundAuditAnswers = {};
  try {
    formData.forEach((value, key) => {
      if (typeof value !== 'string') {
        return;
      }
      const existing = answers[key];
      if (existing === undefined) {
        answers[key] = value;
      } else {
        answers[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
      }
    });
  } catch (error) {
    reportAuditError(error);
  }
  return answers;
}

/**
 * Records a wizard step, discarding that step and anything after it first, so going back and taking a
 * different path only keeps the path that was finally submitted.
 */
export function recordAuditStep(
  audit: RefundAudit,
  stepKey: RefundAuditStepKey,
  answers: RefundAuditAnswers,
): RefundAudit {
  try {
    const { order, name: step } = REFUND_AUDIT_STEPS[stepKey];
    return {
      ...audit,
      steps: [...audit.steps.filter((entry) => entry.order < order), { step, order, answers }],
    };
  } catch (error) {
    reportAuditError(error);
    return withCaptureError(audit, `recordAuditStep:${stepKey}`);
  }
}

function withCaptureError(audit: RefundAudit, captureError: string): RefundAudit {
  try {
    return { ...audit, capture_errors: [...(audit.capture_errors ?? []), captureError] };
  } catch {
    return audit;
  }
}

/**
 * Adds `additional_info.refund_audit` to a refund payload. If the audit can't be attached, a stub carrying only the
 * flow and `capture_errors` is attached instead; if even that fails the payload is returned untouched.
 */
export function withRefundAudit<T extends { additional_info?: object }>(
  payload: T,
  audit: RefundAudit,
  state?: Record<string, unknown>,
): T {
  try {
    const refundAudit: RefundAudit = JSON.parse(JSON.stringify({ ...audit, state }));
    return {
      ...payload,
      additional_info: { ...payload.additional_info, refund_audit: refundAudit },
    };
  } catch (error) {
    reportAuditError(error);
    try {
      const stub: RefundAudit = {
        flow: audit.flow,
        steps: [],
        capture_errors: [...(audit.capture_errors ?? []), 'withRefundAudit'],
      };
      return { ...payload, additional_info: { ...payload.additional_info, refund_audit: stub } };
    } catch {
      return payload;
    }
  }
}

// The legacy modal keeps the agent's entries on one state object (`Refund` in NewModal.tsx) rather than per-step forms.
// Going back overwrites those values in place, so a snapshot at submit only holds the final path.
const LEGACY_REFUND_AUDIT_FIELDS: Record<RefundAuditStepKey, Array<string>> = {
  REASON: ['reason', 'refund_subtype'],
  DETAIL: [
    'variant_of_refund',
    'refund_vendor_agrees_to',
    'percentage',
    'number_of_nights',
    'tmp.amount',
    'tmp.fee',
    'source',
    'mark_cancelled',
    'refund_provider_fee',
    'update_vcc_balance',
    'nib_reference_id',
    'ticket_id',
    'comment',
    'team_leader_name',
    'has_manager_approval',
  ],
  PAYER_ALLOCATION: [],
  CONFIRM: [
    'refund_method',
    'send_refund_notification',
    'send_customer_refund_notification',
    'disable_credits_promo_refund',
  ],
};

function legacyFieldAnswers(refund: object, fields: Array<string>): RefundAuditAnswers {
  const answers: RefundAuditAnswers = {};
  fields.forEach((field) => {
    const value = field.split('.').reduce((current, key) => current?.[key], refund);
    if (value !== undefined && value !== null && value !== '') {
      answers[field] = String(value);
    }
  });
  return answers;
}

export function buildLegacyRefundAudit(refund: object): RefundAudit {
  let audit = createRefundAudit('legacy');
  try {
    (Object.keys(LEGACY_REFUND_AUDIT_FIELDS) as Array<RefundAuditStepKey>).forEach((stepKey) => {
      const answers = legacyFieldAnswers(refund, LEGACY_REFUND_AUDIT_FIELDS[stepKey]);
      if (Object.keys(answers).length) {
        audit = recordAuditStep(audit, stepKey, answers);
      }
    });
  } catch (error) {
    reportAuditError(error);
  }
  return audit;
}
